/**
 * LiveKit data-channel message envelope used by web-device, web-student, web-admin
 * and the local agent bridge. See decisions-log.md D8.
 */
import type { TokenCamera } from "./api.js";

export const DC_PROTOCOL_VERSION = 1 as const;

export type DcType =
  | "raise_hand"
  | "raise_hand_clear"
  | "audio_on"
  | "audio_off"
  | "video_on"
  | "video_off"
  | "text_open"
  | "text_send"
  | "text_close"
  | "presence"
  | "admin_msg"
  | "led_intent"
  | "led_state"
  | "speaker_test"
  | "volume_set"
  | "camera_set"
  | "cameras_set"
  | "hello"
  | "page_alive"
  | "pause"
  | "resume"
  | "cpu";

export interface DcEnvelope<T extends DcType = DcType, P = unknown> {
  v: typeof DC_PROTOCOL_VERSION;
  type: T;
  ts: number; // ms epoch
  payload: P;
}

// --- Payloads ---

export type RaiseHandPayload = { full_name?: string };
export type RaiseHandClearPayload = { reason: "timeout" | "audio" | "video" | "manual" };
export type AudioPayload = Record<string, never>;
export type VideoPayload = Record<string, never>;

export type TextOpenPayload = Record<string, never>;
export type TextSendPayload = { text: string; full_name?: string };
export type TextClosePayload = { by: "teacher" | "student" };

export type PresencePayload = { mode: "invisible" | "visible"; full_name?: string };
/** Where the admin message shows. Omitted ⇒ both kiosk + student (back-compat). */
export type AdminMsgTarget = "kiosk" | "student";
export type AdminMsgPayload = { text: string; targets?: AdminMsgTarget[] };

export type LedIntentPayload = { state: LedStateName };
export type LedStatePayload = { state: LedStateName; color: string; blink: boolean };

export type PageAlivePayload = { ts: number };

/** Teacher pauses the session. Optional `message` shown to the kid/kiosk. */
export interface PausePayload {
  message?: string;
}
/** Teacher resumes the session — carries no payload. */
export type ResumePayload = Record<string, never>;

/**
 * Agent → device-app push: play a short test tone so the operator can confirm
 * the selected speaker actually produces sound. `speaker_label` is the
 * audio-device label to route the tone to via `HTMLMediaElement.setSinkId`
 * (resolved against `MediaDeviceInfo.label`); null → the browser default
 * output. Originates from the admin "Test" button (POST /admin/speaker-test),
 * queued for the agent's next heartbeat, then broadcast over the localhost WS
 * bridge.
 */
export type SpeakerTestPayload = { speaker_label: string | null };

/**
 * Agent → device-app push: set the OS hardware master output volume to an
 * integer `master_volume` in [0,100]. Distinct from the LiveKit remote-audio
 * element gain (`volume` in [0,1]) — this drives the host OS mixer. Originates
 * from an admin change on `PATCH /admin/device-config` or the kiosk's own
 * `PATCH /device/volume`, queued for the agent's next heartbeat, then broadcast
 * over the localhost WS bridge. Mirrors {@link SpeakerTestPayload}'s live-push
 * pattern.
 */
export type VolumeSetPayload = { master_volume: number };

/**
 * Agent → device-app push: switch the PUBLISHED classroom camera live, without
 * a device restart. `camera_label` is the V4L2 card label (resolved from the
 * operator's `/dev/videoN` selection via `resolveCameraLabel`) the device app
 * matches against `MediaDeviceInfo.label` to pick the videoinput deviceId;
 * null → fall back to the browser default camera. Originates from an admin
 * camera change on `PATCH /admin/device-config`, queued for the agent's next
 * heartbeat, then broadcast over the localhost WS bridge. Mirrors
 * {@link SpeakerTestPayload}'s live-push pattern.
 */
export type CameraSetPayload = { camera_label: string | null };

/**
 * Agent → device-app push: set the full list of enabled classroom cameras the
 * kiosk should be publishing (multi-camera). The device app reconciles its
 * published LiveKit video tracks against this list — publishing newly enabled
 * cameras, unpublishing removed ones, and republishing on a name change (the
 * LiveKit track name is immutable post-publish). Supersedes
 * {@link CameraSetPayload}. Originates from an admin change on
 * `PATCH /admin/device-config`, queued for the agent's next heartbeat, then
 * broadcast over the localhost WS bridge.
 */
export type CamerasSetPayload = { cameras: TokenCamera[] };

/**
 * Agent → device-app push: current host CPU load as an integer percent in
 * [0,100], or null when unavailable (non-Linux dev host, /proc/stat parse/IO
 * error, or the first sample with no prior delta baseline). Sampled by the
 * agent over /proc/stat (see agent/src/diy_avatar/cpu.py) and pushed back on
 * each `page_alive` (~5s cadence) over the localhost WS bridge; the initial
 * value also rides the `hello` frame as `cpu`. The kiosk renders it as a
 * colored load badge.
 */
export type CpuPayload = { load: number | null };

export type LedStateName =
  | "ok"
  | "student_connected"
  | "message"
  | "no_internet"
  | "raise_hand"
  | "audio_on"
  | "video_on"
  | "hardware_error"
  | "off";

export const DEFAULT_LED_COLORS: Record<LedStateName, { color: string; blink: boolean }> = {
  ok: { color: "FFFFFF", blink: false },
  student_connected: { color: "00FF00", blink: false },
  message: { color: "00FF00", blink: true },
  no_internet: { color: "FF0000", blink: true },
  raise_hand: { color: "FFA500", blink: true },
  audio_on: { color: "0000FF", blink: false },
  video_on: { color: "00FFFF", blink: false },
  hardware_error: { color: "FF00FF", blink: true },
  off: { color: "000000", blink: false },
};

export function envelope<T extends DcType, P>(type: T, payload: P): DcEnvelope<T, P> {
  return { v: DC_PROTOCOL_VERSION, type, ts: Date.now(), payload };
}

export function parseEnvelope(raw: string | Uint8Array): DcEnvelope | null {
  try {
    const text = typeof raw === "string" ? raw : new TextDecoder().decode(raw);
    const obj = JSON.parse(text);
    if (!obj || typeof obj !== "object") return null;
    if (obj.v !== DC_PROTOCOL_VERSION) return null;
    if (typeof obj.type !== "string") return null;
    if (typeof obj.ts !== "number") return null;
    return obj as DcEnvelope;
  } catch {
    return null;
  }
}
